// React DOM을 HTML에 마운트하고, App 컴포넌트 불러오는 역할

// 기본 라이브러리 먼저 import
import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';

// 스타일 import
import '@mantine/carousel/styles.css';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import './index.css';

// UI 라이브러리 import
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';

// 공유 패키지에서 서비스 import
import { i18n } from '@tickitacka/shared';

// 로컬 컴포넌트 import
import Router, { HandleScroll } from './app/router/index.tsx';
import { RouterSetup } from './app/RouterSetup';
import { LanguageMetadataUpdater } from './common/components/LanguageMetadataUpdater.tsx';
import { ModalRenderer } from './common/components/modals/ModalRenderer';
import { VersionManager } from './common/components/VersionManager.tsx';
import ViewportController from './common/components/ViewportController.tsx';
import { ThemeProvider } from './common/theme/ThemeContext.tsx';
import { queryClient } from './queryClient';

// 전역 unhandled promise rejection 처리
window.addEventListener('unhandledrejection', event => {
    // CancelledError는 React Query의 정상적인 동작이므로 무시
    if (
        event.reason instanceof Error &&
        event.reason.message === 'CancelledError'
    ) {
        console.log('CancelledError caught globally - 정상적인 동작입니다.');
        event.preventDefault(); // 브라우저 콘솔에 에러 표시 방지
        return;
    }
    // 다른 에러는 정상적으로 처리
    console.error('Unhandled promise rejection:', event.reason);
});

const container = document.getElementById('root');
if (!container) {
    throw new Error('Failed to find the root element');
}

createRoot(container).render(
    <StrictMode>
        <I18nextProvider i18n={i18n}>
            <BrowserRouter>
                <RouterSetup />
                <ViewportController />
                <ThemeProvider>
                    <LanguageMetadataUpdater />
                    <MantineProvider
                        theme={{
                            fontFamily:
                                'Pretendard Variable, -apple-system, BlinkMacSystemFont, system-ui, Roboto, "Helvetica Neue", "Segoe UI", "Apple SD Gothic Neo", "Noto Sans KR", "Malgun Gothic", sans-serif',
                            fontFamilyMonospace:
                                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                            fontSizes: {
                                xs: '12px',
                                sm: '14px',
                                md: '16px',
                                lg: '18px',
                                xl: '20px',
                            },
                            breakpoints: {
                                xs: '480px',
                                sm: '768px',
                                md: '900px',
                                lg: '1280px',
                                xl: '1920px',
                            },
                            other: {
                                fontDisplay: 'swap', // 폰트 로딩 최적화
                            },
                            components: {
                                Text: {
                                    styles: {
                                        root: {
                                            whiteSpace: 'pre-wrap',
                                        },
                                    },
                                },
                            },
                        }}
                    >
                        <Notifications />
                        <QueryClientProvider client={queryClient}>
                            <ModalRenderer />
                            <VersionManager>
                                <HandleScroll />
                                <Router />
                            </VersionManager>
                        </QueryClientProvider>
                    </MantineProvider>
                </ThemeProvider>
            </BrowserRouter>
        </I18nextProvider>
    </StrictMode>
);
